/**
 * Utilities > Data Upload — the sales ingestion tab.
 *
 * The history table is this report's own payload (`retail.ingest_files`), so it
 * arrives through the one report path like every other report. The upload
 * control posts to `/api/ingest/files`, which checks the same report grant. The
 * worker loads files in the background; while any row is queued or processing
 * the report query is re-run every few seconds so the status updates in place.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, Upload } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Badge, Button, Card } from '../../components/admin/ui'
import { formatCell, formatNumber } from '../../lib/format'
import { retryIngest, uploadWorkbooks, type UploadResult } from '../../lib/ingest'
import type { Cell } from '../../lib/types'
import type { CustomReportProps } from '../index'

const POLL_MS = 4000

const STATUS_TONE = {
  queued: 'neutral',
  processing: 'warning',
  complete: 'positive',
  failed: 'danger',
} as const

const OUTCOME_TONE = {
  queued: 'positive',
  requeued: 'positive',
  duplicate: 'neutral',
  rejected: 'danger',
} as const

/** Columns shown in the history table, in order. `status` and `error` render specially. */
const SHOWN = [
  'original_filename',
  'status',
  'category',
  'week_ending',
  'data_rows',
  'data_inserted',
  'segmentation_rows',
  'weekly_rows',
  'weekly_through',
  'file_mb',
  'uploaded_by',
  'uploaded_at',
  'finished_at',
] as const

export function DataUpload({ result }: CustomReportProps) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [progress, setProgress] = useState<number | null>(null)
  const [outcomes, setOutcomes] = useState<UploadResult[]>([])

  const index = useMemo(() => {
    const map = new Map<string, number>()
    result.columns.forEach((column, i) => map.set(column.key, i))
    return map
  }, [result.columns])
  const cell = (row: readonly Cell[], key: string): Cell => row[index.get(key) ?? -1] ?? null

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['run', result.slug] })

  const active = result.rows.some((row) => {
    const status = cell(row, 'status')
    return status === 'queued' || status === 'processing'
  })
  useEffect(() => {
    if (!active) return
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['run', result.slug] })
    }, POLL_MS)
    return () => {
      clearInterval(timer)
    }
  }, [active, queryClient, result.slug])

  const upload = useMutation({
    mutationFn: () => uploadWorkbooks(files, setProgress),
    onSuccess: (results) => {
      setOutcomes(results)
      setFiles([])
      if (inputRef.current !== null) inputRef.current.value = ''
      void refresh()
    },
    onSettled: () => {
      setProgress(null)
    },
  })

  const retry = useMutation({
    mutationFn: (fileId: number) => retryIngest(fileId),
    onSuccess: () => void refresh(),
  })

  const columns = SHOWN.map((key) => result.columns[index.get(key) ?? -1]).filter(
    (column) => column !== undefined,
  )

  return (
    <div className="space-y-4">
      <Card
        title="Upload workbooks"
        description="Provider category workbooks (.xlsx with the embedded data model), up to 512 MB each. Files already uploaded are skipped."
      >
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            multiple
            aria-label="Choose workbooks"
            className="text-[length:var(--text-sm)]"
            onChange={(event) => {
              setFiles(Array.from(event.target.files ?? []))
              setOutcomes([])
            }}
          />
          <Button
            variant="primary"
            disabled={files.length === 0 || upload.isPending}
            onClick={() => {
              upload.mutate()
            }}
          >
            <Upload aria-hidden className="size-4" />
            {upload.isPending
              ? `Uploading… ${progress === null ? '' : `${String(Math.round(progress * 100))}%`}`
              : `Upload ${files.length > 0 ? String(files.length) : ''} file${files.length === 1 ? '' : 's'}`}
          </Button>
        </div>
        {progress !== null && (
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-rule)]">
            <div
              className="h-full bg-[var(--accent)] transition-[width]"
              style={{ width: `${String(Math.round(progress * 100))}%` }}
            />
          </div>
        )}
        {upload.error !== null && (
          <p className="mt-3 text-[length:var(--text-sm)] text-[var(--color-negative)]">
            {upload.error.message}
          </p>
        )}
        {outcomes.length > 0 && (
          <ul className="mt-3 space-y-1 text-[length:var(--text-sm)]">
            {outcomes.map((outcome) => {
              // Once a file is in the queue, show its live status from the
              // history rows (refreshed by the poll) rather than the one-off
              // upload response, which would say "queued" forever.
              const row =
                outcome.file_id === null
                  ? undefined
                  : result.rows.find((r) => Number(cell(r, 'id')) === outcome.file_id)
              const live =
                row === undefined || outcome.outcome === 'duplicate'
                  ? undefined
                  : (String(cell(row, 'status')) as keyof typeof STATUS_TONE)
              const error = row === undefined ? null : cell(row, 'error')
              const warning = row === undefined ? null : cell(row, 'warning')
              return (
                <li
                  key={`${outcome.filename}-${String(outcome.file_id)}`}
                  className="flex flex-wrap gap-2"
                >
                  {live === undefined ? (
                    <Badge tone={OUTCOME_TONE[outcome.outcome]}>{outcome.outcome}</Badge>
                  ) : (
                    <Badge tone={STATUS_TONE[live]}>{live}</Badge>
                  )}
                  <span className="font-medium">{outcome.filename}</span>
                  <span
                    className={
                      live === 'failed'
                        ? 'text-[var(--color-negative)]'
                        : 'text-[var(--color-ink-muted)]'
                    }
                  >
                    {live === undefined
                      ? outcome.message
                      : live === 'failed'
                        ? String(error ?? 'Failed')
                        : live === 'complete'
                          ? `Loaded ${formatNumber(Number(cell(row as readonly Cell[], 'data_rows')))} rows.${warning === null ? '' : ` ${String(warning)}`}`
                          : live === 'processing'
                            ? 'Loading…'
                            : 'Waiting in queue.'}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Card
        title="Upload history"
        description={
          active
            ? 'Loading in progress — this list refreshes automatically.'
            : 'Most recent first. Large workbooks take a few minutes to load.'
        }
      >
        {result.rows.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
            Nothing uploaded yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[length:var(--text-sm)]">
              <thead>
                <tr className="border-b border-[var(--color-rule)] text-left text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-2 py-1.5 font-medium ${column.align === 'right' ? 'text-right' : ''}`}
                    >
                      {column.label}
                    </th>
                  ))}
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row) => {
                  const id = Number(cell(row, 'id'))
                  const status = String(cell(row, 'status')) as keyof typeof STATUS_TONE
                  const error = cell(row, 'error')
                  return (
                    <tr key={id} className="border-b border-[var(--color-rule)] align-top">
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`px-2 py-1.5 ${column.align === 'right' ? 'numeric text-right' : ''}`}
                        >
                          {column.key === 'status' ? (
                            <Badge tone={STATUS_TONE[status]}>{status}</Badge>
                          ) : (
                            formatCell(column, cell(row, column.key))
                          )}
                          {column.key === 'original_filename' && error !== null && (
                            <p className="mt-1 max-w-md text-[length:var(--text-xs)] text-[var(--color-negative)]">
                              {String(error)}
                            </p>
                          )}
                          {column.key === 'original_filename' && cell(row, 'warning') !== null && (
                            <p className="mt-1 max-w-md text-[length:var(--text-xs)] text-[#b8860b]">
                              {String(cell(row, 'warning'))}
                            </p>
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-right">
                        {status === 'failed' && (
                          <Button
                            disabled={retry.isPending}
                            onClick={() => {
                              retry.mutate(id)
                            }}
                          >
                            <RotateCcw aria-hidden className="size-3.5" />
                            Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
